import React, { useCallback, useEffect, useRef } from "react";

/* Lightweight, FREE wake-word listener. It uses the browser SpeechRecognition engine ONLY (no paid
   Realtime/WebRTC session is opened while merely waiting). When it hears "Jarvis" it calls onWake(), which
   starts the real session (the existing VoiceSession/LiveSession via the shared voiceRef). It pauses itself
   whenever a session is already active (so there is never a double recognizer or a second paid session, and
   saying "Jarvis" mid-conversation opens nothing new), and resumes when the session ends. Disabling it truly
   stops the recognizer + watchdog and releases the mic; it never cuts an in-progress session.

   Honest limits: needs a user gesture + mic permission, only works while this tab is open & focused, and makes
   NO promise about background / locked-screen listening. Diagnostics are emitted on `jarvis-wake-status`. */

// Turkish pronunciation / near spellings of "Jarvis" (jar-vis, carvis, cervis, carvız …).
const WAKE_RE = /\b(j|c)ar\s?v[iı][sz]\b|\bcer\s?v[iı][sz]\b|\bjarv[iı][sz]\b/i;

const getSR = () => (typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition)) || null;
const emit = (detail) => window.dispatchEvent(new CustomEvent("jarvis-wake-status", { detail }));

export const WakeWord = ({ enabled, sessionActive, onWake }) => {
  const r = useRef({ sr: null, watchdog: null, armed: false, wantOn: false });
  const latest = useRef({ sessionActive, onWake });
  latest.current = { sessionActive, onWake };

  const stopSR = useCallback(() => {
    clearInterval(r.current.watchdog); r.current.watchdog = null;
    const sr = r.current.sr; r.current.sr = null;
    if (sr) { try { sr.onend = null; sr.abort(); } catch (e) {} }
  }, []);

  const startSR = useCallback(() => {
    const SR = getSR();
    if (!SR || !r.current.wantOn || latest.current.sessionActive || r.current.sr) return;
    const sr = new SR();
    sr.lang = "tr-TR"; sr.continuous = true; sr.interimResults = true;
    sr.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const txt = (e.results[i][0] && e.results[i][0].transcript || "").toLowerCase();
        if (WAKE_RE.test(txt)) {
          try { sr.onend = null; sr.abort(); } catch (err) {}
          r.current.sr = null;
          emit({ listening: false, triggered: true });
          if (r.current.wantOn && !latest.current.sessionActive) latest.current.onWake && latest.current.onWake();
          return;
        }
      }
    };
    sr.onerror = (e) => {
      const err = e && e.error;
      if (err === "not-allowed" || err === "service-not-allowed") {
        r.current.wantOn = false; r.current.armed = false;
        stopSR();
        emit({ listening: false, permission: "denied" });
      }
      // transient errors (no-speech/aborted/network): onend re-arms below
    };
    sr.onend = () => {
      if (r.current.sr === sr) {
        r.current.sr = null;
        if (r.current.wantOn && !latest.current.sessionActive) { try { startSR(); } catch (e) {} }
      }
    };
    try {
      sr.start(); r.current.sr = sr;
      emit({ listening: true, supported: true, permission: "granted" });
    } catch (e) { r.current.sr = null; }
  }, [stopSR]);

  const startWatchdog = useCallback(() => {
    clearInterval(r.current.watchdog);
    r.current.watchdog = setInterval(() => {
      if (r.current.wantOn && !latest.current.sessionActive && !r.current.sr) startSR();
    }, 4000);
  }, [startSR]);

  useEffect(() => {
    const SR = getSR();
    // fully off
    if (!enabled) {
      r.current.wantOn = false;
      stopSR();
      emit({ enabled: false, listening: false, supported: !!SR });
      return;
    }
    emit({ enabled: true, supported: !!SR });
    if (!SR) { emit({ supported: false }); return; }
    // a session is running → pause the wake listener (do NOT cut the session); resume when it ends
    if (sessionActive) {
      clearInterval(r.current.watchdog); r.current.watchdog = null;
      const sr = r.current.sr; r.current.sr = null;
      if (sr) { try { sr.onend = null; sr.abort(); } catch (e) {} }
      emit({ listening: false, reason: "session" });
      return;
    }
    // want to listen; SpeechRecognition needs mic permission (prompted on a user gesture)
    r.current.wantOn = true;
    if (r.current.armed) { startSR(); startWatchdog(); }
    else emit({ needsGesture: true, listening: false });
  }, [enabled, sessionActive, startSR, startWatchdog, stopSR]);

  // One tab-level gesture arms the recognizer (so enabling via the toggle click already counts).
  useEffect(() => {
    const onGesture = () => {
      r.current.armed = true;
      if (r.current.wantOn && !latest.current.sessionActive && !r.current.sr) { startSR(); startWatchdog(); }
    };
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("keydown", onGesture);
    return () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
    };
  }, [startSR, startWatchdog]);

  useEffect(() => () => { r.current.wantOn = false; stopSR(); }, [stopSR]);

  return null;
};

export default WakeWord;
